'use client';

import React, { memo, useMemo } from 'react';
import { Modal, Text } from '@shopify/polaris';
import { MediaAsset, MediaAssetType, CombinedGenerationConfiguration } from '@/types/media';
import VideoAssetPreview from '@/features/media-modal/components/common/VideoAssetPreview';
import { VideoAssetSettings } from '@/features/ai-video-creator/stores/productMediaSlice';
import { Image } from './Image';
import GenerationSettingsPanel from './GenerationSettingsPanel';
import { StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';

interface MediaModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  mediaAssets: MediaAsset[];
  studioVideo?: StudioVideo | null;
  activeAssetId?: string | null;
  setActiveAssetId?: (id: string) => void;
  updateMediaAsset?: (asset: MediaAsset, index: number) => void;
}

const MediaPreviewDisplay = memo(
  ({
    mediaAsset,
    updateMediaAsset,
    activeAssetIndex,
  }: {
    mediaAsset: MediaAsset;
    updateMediaAsset?: (asset: MediaAsset, index: number) => void;
    activeAssetIndex: number;
  }) => {
    const onVideoSettingsChange = (settings: VideoAssetSettings) =>
      updateMediaAsset?.({ ...mediaAsset, settings }, activeAssetIndex);

    return (
      <div className="relative flex items-center justify-center w-full h-full overflow-hidden">
        {mediaAsset.type === MediaAssetType.image ? (
          <div className="relative flex flex-col w-full h-full overflow-hidden">
            <div className="flex flex-col flex-1 w-full">
              <div className="relative mx-auto aspect-[9/16] flex-1 bg-black">
                <Image
                  src={mediaAsset.src}
                  alt={mediaAsset.alt || ''}
                  className="block object-contain max-w-full max-h-full"
                  fill
                  size="large"
                />
              </div>
            </div>
          </div>
        ) : mediaAsset.type === MediaAssetType.video ? (
          <VideoAssetPreview
            video={mediaAsset}
            onSettingsChange={updateMediaAsset ? onVideoSettingsChange : undefined}
          />
        ) : null}
      </div>
    );
  }
);
MediaPreviewDisplay.displayName = 'MediaPreviewDisplay';

const MediaPreviewModal = ({
  isModalOpen,
  closeModal,
  mediaAssets,
  activeAssetId,
  updateMediaAsset,
  studioVideo,
}: MediaModalProps) => {
  const activeAssetIndex = useMemo(
    () => mediaAssets.findIndex((asset) => asset.id === activeAssetId),
    [mediaAssets, activeAssetId]
  );

  const activeAsset = useMemo(
    () => (activeAssetIndex !== -1 && activeAssetIndex < mediaAssets.length ? mediaAssets[activeAssetIndex] : null),
    [mediaAssets, activeAssetIndex]
  );

  const activeStudioVideo = useMemo(
    () => (studioVideo && studioVideo.id === activeAssetId ? studioVideo : null),
    [studioVideo, activeAssetId]
  );

  const previewAreaBackground =
    activeAsset || activeStudioVideo ? 'var(--p-color-bg-surface-secondary)' : 'var(--p-color-bg-surface)';

  return (
    <Modal
      open={isModalOpen}
      onClose={closeModal}
      title={activeAsset ? `Previewing ${activeAsset.name || activeAsset.type}` : 'Media Preview'}
      titleHidden
      size="large"
    >
      <div className="h-[calc(90vh-60px)] max-h-[800px] overflow-y-auto">
        <div className="flex flex-col h-full min-h-0 gap-4 md:flex-row">
          {(activeAsset || activeStudioVideo) && (
            <div className="w-full h-auto md:h-full md:w-1/3">
              <GenerationSettingsPanel
                mediaAsset={activeAsset}
                assetName={activeAsset?.name}
                studioVideo={activeStudioVideo}
              />
            </div>
          )}
          <div className={`w-full ${activeAsset || activeStudioVideo ? 'md:w-2/3' : 'md:w-full'} h-[60vh] md:h-full`}>
            <div className="flex items-center justify-center h-full p-5" style={{ background: previewAreaBackground }}>
              {activeAsset ? (
                <MediaPreviewDisplay
                  mediaAsset={activeAsset}
                  updateMediaAsset={updateMediaAsset}
                  activeAssetIndex={mediaAssets.findIndex((asset) => asset.id === activeAssetId)}
                />
              ) : (
                <Text variant="bodyMd" as="p">
                  No media selected.
                </Text>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default memo(MediaPreviewModal);
